import {
  AlertColor,
  Box,
  Button,
  styled,
  TextField,
  Theme,
  useMediaQuery,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { globalTheme } from "../../../common/theme";
import createStyles from "@mui/styles/createStyles";
import axios from "axios";
import React from "react";
import { SnackBarMessage, SnackBarMessageProps } from "../../component/SnackBarMessage";
import Title from "../../component/Title";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { GridCellParams, GridColumns, GridPreProcessEditCellProps, GridToolbarContainer, GridValueSetterParams } from "@mui/x-data-grid";
import { Add } from "@mui/icons-material";
import { renewToken } from "../../../services/AuthService";
import { useConstructor } from "../../../common/UseConstructor";
import { Currency, ExchangeRateInfo } from "./ExchangeRateAccessories";
import { FormatNumber } from "../../../common/Formatter";
import { ValidateNumber } from "../../../common/CommonFunctions";
import { NumberTypes } from "../../../models/enums/NumberType";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
  })
);

const StyledBox = styled(Box)(({ theme }) => ({
  height: "100%",
  width: "100%",
  "& .super-app-theme--cell": {
    backgroundColor: "rgba(41, 67, 63, 0.90)"
  },
}));

export function ExchangeRateManagement() {
  const style = { ...useStyles(), ...globalTheme() };

  const tinyScreen = useMediaQuery('(max-width:1366px)');
  const smallScreen = useMediaQuery('(max-width:1920px)');
  const normalScreen = useMediaQuery('(max-width:2560px)');

  const [loading, setLoading] = React.useState<boolean>(false);

  const hasChanges = React.useRef<boolean>(false);

  const [gridData, setGridData] = React.useState<ExchangeRateInfo[]>([]);

  //Grid columns
  const columns: GridColumns = [
    { 
      field: 'currencyDescription', headerName: 'Currency', flex: 1, type: 'string',
        cellClassName: (params: GridCellParams<number>) => {
          if (params.value == null) {
            return "";
          }

          if (params.row.id == 0)
            return "super-app-theme--cell";
          else
            return "";
      }
    },
    {
      field: 'month', headerName: 'Month', flex: 1, disableColumnMenu: true, sortable: false,
      type: 'number', align: 'right',
      cellClassName: style.NumberTextField
    },
    {
      field: 'exRate', headerName: 'Exchange Rate', flex: 2, disableColumnMenu: true, sortable: false,
      type: 'number', editable: true, align: 'right',
      valueFormatter: (param) => FormatNumber(param.value, 4),
      valueSetter: onExchangeRateChanged,
      preProcessEditCellProps: validateNumberValueChanged,
      cellClassName: style.NumberTextField
    },    
  ];

  function validateNumberValueChanged(param: GridPreProcessEditCellProps) {
    let errMsg: string = "";

    errMsg = ValidateNumber(param.props.value.toString(), NumberTypes.FLOAT, true);

    if (errMsg !== "")
      showSnackBar(errMsg, "", "error");

    return { ...param.props, error: errMsg !== "" };
  }

  function onExchangeRateChanged(param: GridValueSetterParams) {
    let row: ExchangeRateInfo = param.row;
    row.exRate = parseFloat(param.value.toString().replace(/,/g, ''));

    let tmpData = gridData;
    let index = tmpData.findIndex(x => x.currencyDescription === row.currencyDescription && x.month === row.month);
    tmpData[index].exRate = row.exRate;
    setGridData(tmpData);

    hasChanges.current = true;

    EditExchangeRate(row);
    return { ...param.row, row };
  }

  
  //Dialogs and snacks
  const [snackSettings, setSnackSettings] =
    React.useState<SnackBarMessageProps>({
      open: false,
      message: "",
      subMessage: "",
      severity: undefined,
      onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
    });

  function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
    setSnackSettings({
      open: true,
      message: message,
      subMessage: subMessage,
      severity: colour,
      onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
    });
  }

  function EditExchangeRate(exchangeRate: ExchangeRateInfo | undefined) {
    if (!exchangeRate)
      return;

    let ccy: Currency = {currencyDescription: exchangeRate.currencyDescription, month: exchangeRate.month, exRate: exchangeRate.exRate};  

    axios.put<ExchangeRateInfo>('Api/V1/ExchangeRate', ccy).then(
      (result) => {
        if(result.data)
        {
          hasChanges.current = false;
          showSnackBar("Exchange rate updated successfully.", "", "success");
        }
      },
      (error) => {
        showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
      }
    );
  }

  function SearchExchangeRate() {
    setLoading(true);

    axios.get<ExchangeRateInfo[]>('Api/V1/ExchangeRate', {
      headers: {
        'Content-Encoding': 'gzip'
      }
    })
      .then((result) => {
        setGridData(result.data)
        setLoading(false);
      },
        (error) => {
          setGridData([]);
          setLoading(false);
          console.log(error);
          showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
        }
      )
      .catch((error) => {
        setLoading(false);
        console.log(error);
        showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
      });
  }

  useConstructor(() => Initialise())
  function Initialise() {
    renewToken();
    
    hasChanges.current = false;
    SearchExchangeRate();
  }

  return (
      <div className={style.PageMain}>
          <Title pageTitle="Exchange Rate Management" />
          <div className={style.PageContent}>
            <div className={style.ModalContent}>
              <div className={style.PageFieldsFullHeight}>
                <div className={style.ModalContentFieldOneThirdWidth} style={{minWidth: 300}}>
                  <StyledBox>
                    <DataGridPro
                      getRowId={(row) => row.currencyDescription}
                      rows={gridData}
                      columns={columns}
                      loading={loading}
                      rowHeight={tinyScreen ? 20 : (smallScreen ? 24 : 28)}
                    />
                  </StyledBox>
                </div>
              </div>
            </div>
          </div>

        <SnackBarMessage {...snackSettings} />
      </div>
  );


}

